import { Notification } from "@vds/notifications";
import { Title } from "@vds/typography";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../../global/utility/permissionUtils";
import { showNotificationBanner, sitesModified } from "../../store/actions";
import "../../styles/deviceStyle.css";
import { SettingsTile } from "./SettingsTile";
import { ROUTES } from "../../global/constants/routesConstants";
import { NOTIFICATION_TYPES, PERMISSION_TYPES, SETTINGS_DESCRIPTION, SETTINGS_ICONS, SETTINGS_TITLE, VIEW_TEXT } from "./constants";
import { NO_PERMISSION, SEVERITY_LEVELS } from "../../global/constants/constants";
import { clearDetailNavSource } from "../../hooks/usePaginationReset";
import styled from "styled-components";
import { device } from "../../styles/breakpoints";

const SettingsContainer = styled.div`
  padding: 0 16px;

  @media ${device.mobile} {
    padding: 0;
  }
`;

const SettingsHeader = styled.div`
  padding: 16px 0 24px;
`;

const Settings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isV1OnlyAccount = useSelector((state) => state.licenseReducer.isV1OnlyAccount);

  useEffect(() => {
    clearDetailNavSource();
  }, []);

  const [notification, setNotification] = useState({
    opened: false,
    type: NOTIFICATION_TYPES.WARNING,
    message: NOTIFICATION_TYPES.MESSAGE,
  });
  const [showNotification, setShowNotification] = useState(false);
  useEffect(() => {
    if (notification.opened) {
      const hideNotification = () => {
        setTimeout(() => {
          setNotification({ ...notification, opened: false });
        }, 3000);
      };
      hideNotification();
    }
  }, [notification]);

  const Sites = () => {
    if (!hasPermission(PERMISSION_TYPES.SITES, VIEW_TEXT)) {
      showPermissionsBanner();
    } else {
      navigate("/Sites");
    }
  };
  const users = () => {
    if (!hasPermission(PERMISSION_TYPES.USERS, VIEW_TEXT)) {
      showPermissionsBanner();
    } else {
      navigate(ROUTES.USERGROUPS);
    }
  };
  const support = () => {
    navigate(ROUTES.SUPPORT);
  };

  const licenses = () => {
    if (!hasPermission(PERMISSION_TYPES.LICENSES, VIEW_TEXT)) {
      showPermissionsBanner();
    } else {
      navigate(ROUTES.LICENSES);
    }
  };

  const handleEmptyClick = () => {
    setNotification({ ...notification, opened: true });
  };

  const roles = () => {
    if (!hasPermission(PERMISSION_TYPES.ROLES, VIEW_TEXT)) {
      showPermissionsBanner();
    } else {
      navigate(ROUTES.ROLES);
    }
  };

  const Rules = () => {
    if (!hasPermission(PERMISSION_TYPES.ALERTS, VIEW_TEXT)) {
      showPermissionsBanner();
    } else {
      navigate(ROUTES.ALERTS);
    }
  };

  const entrolledAppearance = () => {
    // if (!hasPermission("Enrolled Appearance", "View")) {
    //   showPermissionsBanner();
    // } else {
      navigate(ROUTES.ENROLLED_APPEARANCE);
    //}
  }

  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: NO_PERMISSION,
      priority: SEVERITY_LEVELS.CRITICAL,
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  useEffect(() => {
    if (notification.opened) {
      const hideNotification = () => {
        setTimeout(() => {
          setNotification({ ...notification, opened: false });
        }, 3000);
      };
      hideNotification();
    }
  }, [notification]);

  //reproduce error boundary error
  // useEffect(() => {
  //   dispatch(sitesModified(runtime));
  // }, []);

  return (
    <>
      <SettingsContainer data-testid="sasp-network-page">
        {notification?.opened && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1050,
              display: "flex",
              justifyContent: "center",
              width: "auto",
            }}
          >
            <Notification
              type={notification.type}
              title={notification.message}
              fullBleed={false}
              inline={false}
              disableFocus={true}
              data-testid="settings_notificationCloseButton"
              onCloseButtonClick={() =>
                setNotification({ ...notification, opened: false })
              }
            />
          </div>
        )}
        <SettingsHeader>
          <Title bold={true} size="large" data-testid="sasp-network-page-title">
            Settings
          </Title>
        </SettingsHeader>

        {/*This entire div is commented for now as per figma please use these components if needed in future for APPS and FIRMWARE }
        {/* <div className="sasp_d-flex sasp_config-tiles-wrapper">
          <SettingsTile
            icon="advanced-settings"
            title="Apps"
            dataTestId="sasp-settings-apps-tile"
            description="View CV analytics deployed across your sites"
            onClick={handleEmptyClick}
          />
          <SettingsTile
            icon="network"
            title="Firmware"
            dataTestId="sasp-settings-network-tile"
            description="Deliver cellular-device firmware over the air (FoTA)"
            onClick={handleEmptyClick}
          />
        </div> */}
        <div className="sasp_d-flex sasp_config-tiles-wrapper">
          <SettingsTile
            icon={SETTINGS_ICONS.ALERTS}
            title={SETTINGS_TITLE.ALERTS}
            dataTestId="sasp-settings-rules-tile"
            description={SETTINGS_DESCRIPTION.ALERTS}
            data-testid="support_rulesSettingsTile"
            onClick={Rules}
          />
          <SettingsTile
            icon={SETTINGS_ICONS.LICENSES}
            title={SETTINGS_TITLE.LICENSES}
            dataTestId="sasp-settings-support-tile"
            description={SETTINGS_DESCRIPTION.LICENSES}
            data-testid="support_licensesSettingsTile"
            onClick={licenses}
          />
        </div>
        <div className="sasp_d-flex sasp_config-tiles-wrapper">
          <SettingsTile
            icon={SETTINGS_ICONS.ROLES}
            title={SETTINGS_TITLE.ROLES}
            dataTestId="sasp-settings-support-tile"
            description={SETTINGS_DESCRIPTION.ROLES}
            data-testid="support_rolesSettingsTile"
            onClick={roles}
          />
          <SettingsTile
            icon={SETTINGS_ICONS.SITES}
            title={SETTINGS_TITLE.SITES}
            dataTestId="sasp-settings-sites-tile"
            description={SETTINGS_DESCRIPTION.SITES}
            data-testid="support_sitesSettingsTile"
            onClick={Sites}
          />
        </div>
        <div className="sasp_d-flex sasp_config-tiles-wrapper">
          <SettingsTile
            icon={SETTINGS_ICONS.SUPPORT}
            title={SETTINGS_TITLE.SUPPORT}
            dataTestId="sasp-settings-support-tile"
            description={SETTINGS_DESCRIPTION.SUPPORT}
            data-testid="support_supportSettingsTile"
            onClick={support}
          />
          <SettingsTile
            icon={SETTINGS_ICONS.USERS}
            title={SETTINGS_TITLE.USERS}
            dataTestId="sasp-settings-users-tile"
            description={SETTINGS_DESCRIPTION.USERS}
            data-testid="support_usersSettingsTile"
            onClick={users}
          />
        </div>
          {window.REACT_APP_ENROLLED_APPEARANCE === "true" && !isV1OnlyAccount && (
            <div className="sasp_d-flex sasp_config-tiles-wrapper">
              <SettingsTile
                icon={SETTINGS_ICONS.ENROLLED_APPEARANCE}
                title={SETTINGS_TITLE.ENROLLED_APPEARANCE}
                dataTestId="sasp-settings-support-tile"
                description={SETTINGS_DESCRIPTION.ENROLLED_APPEARANCE}
                data-testid="support_supportSettingsTile"
                onClick={entrolledAppearance}
              />
            <span className="single-tile-settings" />
            </div>
          )}
      </SettingsContainer>
    </>
  );
};

export default Settings;
